/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React, { useState } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { MENU_HEIGHT } from 'src/components/css'
import EditPlayerModal from './player/setupModal/EditPlayerModal'
import NotificationSettingsModal from '../../components/NotificationSettingsModal'
import { useProfileContext } from './context/ProfileContext'
import { useProfilePages } from './hooks/useProfilePages'
import ProfileUserRow from './components/ProfileUserRow'
import ProfilePlayerRow from './components/ProfilePlayerRow'
import ProfileNavBar from './components/ProfileNavBar'
import NoPlayerProfile from './components/NoPlayerProfile'
import FamilyManagementModal from './family/FamilyManagementModal'
import { CONTENT_GUTTER, MAX_CONTENT_WIDTH } from './utils/constants'
import { hasNoAssociatedPlayers } from './utils/playerAssociation'

const ProfilePage: React.FC = () => {
  const {
    item,
    players,
    loadedPlayers,
    selectedPlayer,
    selectedPlayerId,
    selectPlayer,
    isPrimaryForSelectedPlayer,
    suspensions,
    suspensionLoading,
    isSuspended,
    isNotificationSettingsOpen,
    closeNotificationSettings,
  } = useProfileContext()

  const [isFamilyOpen, setIsFamilyOpen] = useState(false)
  const [isEditPlayerOpen, setIsEditPlayerOpen] = useState(false)

  const pages = useProfilePages({
    selectedPlayerId,
    suspensionLoading,
    suspensions,
  })

  const noPlayers = hasNoAssociatedPlayers({
    loadedPlayers,
    playerCount: players.length,
  })

  return (
    <div css={noPlayers ? emptyPageStyle : pageStyle}>
      <ProfileUserRow
        item={item}
        isSuspended={isSuspended}
        suspensions={suspensions}
      />

      {noPlayers ? (
        <div css={emptyStateWrapperStyle}>
          <NoPlayerProfile />
        </div>
      ) : (
        <>
          <ProfilePlayerRow
            player={selectedPlayer}
            players={players}
            selectedPlayerId={selectedPlayerId}
            onSelectPlayer={selectPlayer}
            onEditPlayer={() => {
              setIsEditPlayerOpen(true)
            }}
            onOpenFamily={() => setIsFamilyOpen(true)}
            canEdit={isPrimaryForSelectedPlayer}
          />
          <ProfileNavBar pages={pages} />

          <div css={tabContentStyle}>
            {loadedPlayers && (
              <Switch>
                {/* The profile opens on Stats; the query string comes along. */}
                <Route
                  exact
                  path={ROUTES.PROFILE.ROOT}
                  render={({ location }) => (
                    <Redirect
                      to={{
                        pathname: ROUTES.PROFILE.STATS,
                        search: location.search,
                      }}
                    />
                  )}
                />
                {pages.map((page) => (
                  <Route key={page.to} exact path={page.to}>
                    {page.component}
                  </Route>
                ))}
              </Switch>
            )}
          </div>
        </>
      )}

      <FamilyManagementModal
        isOpen={isFamilyOpen}
        onClose={() => setIsFamilyOpen(false)}
        playerId={selectedPlayerId}
        canManage={isPrimaryForSelectedPlayer}
      />

      <EditPlayerModal
        isOpen={isEditPlayerOpen}
        onClose={() => setIsEditPlayerOpen(false)}
        playerId={selectedPlayerId ?? undefined}
      />
      <NotificationSettingsModal
        isOpen={isNotificationSettingsOpen}
        onClose={closeNotificationSettings}
      />
    </div>
  )
}

export default ProfilePage

const pageStyle = css`
  min-height: 100vh;
  background-color: ${colors.SECONDARY_500};
  margin-bottom: 100px;
`

// Empty state has no scrollable content, so fill exactly the area below the top
// nav (no 100px bottom margin) to avoid a dead vertical scrollbar.
const emptyPageStyle = css`
  display: flex;
  flex-direction: column;
  height: calc(100vh - ${MENU_HEIGHT}px);
  background-color: ${colors.SECONDARY_500};
`

const tabContentStyle = css`
  max-width: ${MAX_CONTENT_WIDTH}px;
  margin: 0 auto;
  padding-left: ${CONTENT_GUTTER}px;
  padding-right: ${CONTENT_GUTTER}px;
`

const emptyStateWrapperStyle = css`
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
  width: 100%;
  max-width: ${MAX_CONTENT_WIDTH}px;
  margin: 0 auto;
  padding: 24px ${CONTENT_GUTTER}px;
  display: flex;
  flex-direction: column;
`
